import { ALTURA_DA_MARCA, cabecalhoResolvido } from '@vestigo/core/area-cabecalho-modelos';
import { EscolhaDoIdioma } from './EscolhaDoIdioma.js';
import { useT } from '../lib/idioma.js';
import { atributosDoLogo, type ImagemNaTela } from '@vestigo/core/area-marca';
import { Suspense, lazy, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type ItemDeMenu } from '../lib/api.js';
import { lerAmbienteDoMovimento, podeAnimar } from '../lib/capa-animada.js';
import { PortaFlutuante } from '../doca/PortaFlutuante.js';
import { useDoTopo } from './links-do-topo.js';
import { telaComDicionario } from '../lib/tela-com-dicionario.js';

/**
 * ── A PORTA DA BUSCA VIVE AQUI, E O PAINEL NÃO ─────────────────────────────
 *
 * O painel inteiro — resultados, teclado, conversa — chega por `lazy()`, quando
 * alguém abre a busca. O que o pedaço de entrada paga é o botão e a tecla.
 *
 * **Por que no cabeçalho, contra as três alternativas medidas:**
 *
 * | onde | por que não |
 * |---|---|
 * | uma quarta porta em `PortasDaArea` | ela monta em **2 das 11 rotas** (a vitrine e o mural). Quem está perdido não está na vitrine — e procurar não é uma *secção* a que se vai, é um gesto que se invoca e se dispensa sem perder o sítio |
 * | uma rota `/a/:slug/buscar` | procurar custaria o lugar onde a pessoa estava, e voltar seria um segundo gesto |
 * | um botão flutuante no canto | no telemóvel a sala já tem a alça da Doca colada em baixo, e duas barras fixas no mesmo rodapé é o defeito que `FolhaDaDoca` existe para não cometer |
 *
 * O cabeçalho é a única mobília que **toda** tela da área tem. A busca aparece
 * onde a área aparece, e some onde não há área: `/perfil` não a passa (não tem
 * área), e a **prévia do Estúdio** também não — ali não há sessão de aluno, e
 * uma busca que responde 401 dentro da tela de desenho do tutor é pior do que
 * busca nenhuma.
 */
const PainelDeBusca = telaComDicionario(() =>
  import('../busca/Painel.js').then((m) => ({ default: m.PainelDeBusca })),
);

/**
 * O cabeçalho da área — e o logo do cliente quando ele existe.
 *
 * ## Por que mora num arquivo próprio
 *
 * Porque a Prova do Estúdio precisa dele. Enquanto ele vivia dentro de
 * `App.tsx`, a prévia que o dono usa para julgar a área não tinha cabeçalho — e
 * o logo que ele acabou de escolher na marginália não aparecia em lugar nenhum
 * até ele abrir a área como aluno. Importar de `App.tsx` fecharia um ciclo
 * (`App` → `Previa` → `App`); um componente próprio é a saída sem réplica.
 *
 * ## Por que o logo SUBSTITUI o nome, e não fica ao lado
 *
 * Porque os dois dizem a mesma coisa, e um logo com o nome escrito ao lado é a
 * mesma palavra duas vezes. O `alt` continua sendo o nome: para quem usa leitor
 * de tela, e para o caso de a imagem não carregar, o cabeçalho volta a ser texto.
 */
/**
 * O alvo dos links do cabeçalho — 44px de altura, sem fundo em repouso.
 *
 * Eles eram `text-sm` puro, e a auditoria os mediu **a 20px de altura em 390**:
 * `Suporte` 49×20, `Áreas` 34×20, `Conta` 37×20, `Sair` 23×20. São os controles
 * de conta do produto, num dedo de celular, com menos da metade do alvo mínimo —
 * e `Sair` ao lado de `Conta` com 20px é o par em que errar o dedo custa a
 * sessão. O texto não muda de tamanho; o que muda é a caixa clicável em volta,
 * que é a mesma decisão que `PortasDaArea` já tinha tomado.
 */
const ALVO =
  /* `min-w-[44px]` junto com a altura: `Sair` tem quatro letras, e só a altura
     deixava um alvo de 39×44 — alto o bastante e estreito demais. */
  'inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-md px-3 text-sm transition-colors hover:bg-[color-mix(in_srgb,var(--texto-suave)_16%,transparent)]';

/**
 * A linha de uma ação dentro da gaveta do celular — o mesmo alvo, deitado.
 *
 * A gaveta é uma coluna, então aqui o alvo grande vem da **altura** e a largura
 * é a da gaveta inteira: ao contrário da barra, não há concorrência por largura
 * lá dentro, e um alvo de 224×44 é o que se acerta com o polegar sem olhar.
 */
const LINHA_DA_GAVETA =
  'flex min-h-[44px] w-full items-center rounded-md px-3 text-left text-sm transition-colors hover:bg-[color-mix(in_srgb,var(--texto-suave)_16%,transparent)]';

export function Cabecalho({
  titulo,
  voltar,
  logo = null,
  menu = [],
  acoes = true,
  portas = null,
  encontro = null,
  area = null,
  assistente,
  cabecalho: cabecalhoDado,
}: {
  titulo: string;
  /** O modelo, a marca e o fixo (S5); ausente = clássico. Ver `cabecalho-modelos.ts`. */
  cabecalho?: { modelo?: string; marca?: string; fixo?: boolean } | null;
  voltar?: string;
  /**
   * O logo **desembrulhado** — nunca o objeto cru da rota, nunca uma string.
   *
   * O tipo é `ImagemNaTela` (tem `src`) e não `ImagemDaMarca` (tem `url`) de
   * propósito: é o que faz o compilador recusar `logo={dados.marca.logo}` sem
   * passar por `marcaDaArea`, que é o gesto que faltou e que pôs
   * `<img src="[object Object]">` no ar. Nulo = o cliente não escolheu logo, e
   * o cabeçalho volta a ser o nome da área em texto.
   */
  logo?: ImagemNaTela | null;
  /**
   * Até seis links do dono: suporte, comunidade, o grupo do WhatsApp.
   *
   * O teto e a validação do endereço são do servidor (`menuDaArea`, no core):
   * `url` só passa se for `http`, `https`, `mailto:` ou um caminho começando em
   * `/`. É o que impede um `javascript:` colado no painel de virar execução de
   * script na sessão de todo aluno da área — e é por isso que esta tela pode
   * jogar o valor num `href` sem conferir nada.
   */
  menu?: ItemDeMenu[];
  /**
   * O interruptor do tutor nesta área, quando quem desenha já o sabe.
   *
   * A vitrine sabe (vem na resposta dela) e passa; as outras telas não passam e
   * o cabeçalho pergunta por si, pelo mesmo pedido dos links — ver
   * `useDoTopo`. `undefined` é «ninguém me disse», e nunca «não».
   */
  assistente?: boolean | undefined;
  /**
   * "Áreas" e "Sair".
   *
   * Desligadas na Prova do Estúdio: quem edita não tem sessão de aluno, e um
   * "Sair" clicável dentro do iframe levaria a prévia para a tela de login —
   * exatamente o defeito que a rota `/previa` existe para consertar.
   */
  acoes?: boolean;
  /**
   * As portas da área — Agenda, Comunidade, Trilhas —, na MESMA linha do nome.
   *
   * Elas ocupavam uma faixa inteira abaixo do cabeçalho, e essa faixa empurrava o
   * herói, a saudação e as prateleiras para baixo sem dizer nada que o cabeçalho
   * não pudesse dizer junto. São três links; três links não precisam de uma linha
   * própria numa página cuja primeira tela é o produto.
   *
   * Vêm como `children` e não como dados porque quem sabe quais portas existem é
   * a vitrine (uma área sem trilha não mostra `Trilhas`), e porque a `PortasDaArea`
   * também busca a agenda para saber se há live agora — o cabeçalho não deve
   * aprender nada disso para servir de moldura.
   */
  portas?: React.ReactNode;
  /**
   * ── A PASTILHA DO PRÓXIMO ENCONTRO, À ESQUERDA DE `Procurar` (25/09/2026) ──
   *
   * Ordem do dono: *«essa notificação toma muito espaço, coloque ao lado
   * esquerdo de procurar ou do lado de trilhas na barra superior»*. O que chega
   * aqui é `PastilhaDoEncontro` — e o cabeçalho continua a não saber nada de
   * agendas, pelo mesmo motivo que não sabe nada de trilhas: quem sabe se há
   * encontro é quem já pediu a agenda.
   *
   * **Vem por `prop`, e não por `import` aqui dentro, por causa da rede.** Este
   * cabeçalho monta em onze rotas. Uma pastilha que vivesse dentro dele punha as
   * onze a pedir `/a/:slug/agenda` — um pedido novo em cada tela da área por uma
   * peça que só a entrada precisa. Ausente = esta tela não a mostra, que é o
   * caso de dez das onze, e o caso da prévia do Estúdio.
   *
   * **A barra não cresce por ela**: o alvo dela tem 44 px, que é a altura que
   * `ALVO` já dá a todo o resto desta linha, e a altura do cabeçalho é `py-3`
   * mais o filho mais alto. 68 px antes, 68 px depois.
   */
  encontro?: React.ReactNode;
  /**
   * O slug da área — e ele existe aqui por **uma** razão: a busca.
   *
   * Ausente = esta tela não tem área (a conta) ou não tem sessão de aluno (a
   * prévia do Estúdio), e então não há busca. É um `null` que decide, e não um
   * esquecimento: ver o cabeçalho de `PainelDeBusca`.
   */
  area?: string | null;
}) {
  const navegar = useNavigate();
  /* A lingua desta sessao, resolvida pelo servidor e guardada uma vez pelo
     `ProvedorDeIdioma`. Area monolingue devolve portugues, que e o fallback. */
  const t = useT();
  const [gaveta, setGaveta] = useState(false);
  const [busca, setBusca] = useState(false);
  const cab = cabecalhoResolvido(cabecalhoDado);
  /* Mínimo: os links e a conta moram na gaveta em QUALQUER largura. */
  const semBarra = cab.modelo === 'minimo';
  const centrado = cab.modelo === 'centrado';
  const alturaDaMarca = ALTURA_DA_MARCA[cab.marca];
  /*
   * ── O LOGO QUE SE MEXE, E QUEM PEDIU QUE NADA SE MEXA ───────────────────
   *
   * `animada` é um fato do servidor (o arquivo é `image/gif`), e o produto já
   * tem a regra: `podeAnimar` recusa movimento a quem ligou
   * `prefers-reduced-motion` ou economia de dados. No cartaz a recusa serve o
   * primeiro quadro; aqui não há primeiro quadro para servir — GIF não deriva
   * (`derivados-do-asset.ts` diz isso com todas as letras), então `srcset` e
   * `padrao` são o próprio GIF, e um GIF não pausa: parar é tirar o `src`.
   *
   * O cabeçalho é o único lugar onde tirar o `src` tem chão embaixo: o ramo de
   * baixo desenha o NOME da área, que é o mesmo que ele desenha quando não há
   * logo nenhum. Perde-se a imagem da marca — e é uma perda, dita aqui para
   * ninguém a descobrir por acaso —, não se perde a identificação da área. Um
   * logo em laço no topo de TODA página é exatamente o padrão que a preferência
   * existe para impedir.
   *
   * Um logo parado (a esmagadora maioria: PNG, JPEG, SVG) não passa por aqui —
   * `animada` é `false` e nada muda. E `lerAmbienteDoMovimento` já está no
   * pedaço de entrada por causa da vitrine: zero byte novo.
   */
  const logoDesenhavel =
    logo && logo.animada && !podeAnimar(lerAmbienteDoMovimento()) ? null : logo;
  const gavetaId = useId();
  const caixa = useRef<HTMLDivElement>(null);

  async function sair() {
    await api.post('/sair').catch(() => undefined);
    navegar('/entrar', { replace: true });
  }

  /*
   * A gaveta fecha por fora e pelo `Esc`.
   *
   * Um menu que só fecha pelo próprio botão é um menu que fica aberto por cima
   * da página inteira quando a pessoa desiste dele — e no celular ele cobre a
   * primeira capa da vitrine. O `capture` não é necessário aqui: a gaveta não
   * vive dentro de shadow root nenhum, e o clique nos próprios itens já fecha
   * antes de o documento ver o evento (`fechar` roda no `onClick` deles).
   */
  useEffect(() => {
    if (!gaveta) return;
    const foraDaqui = (e: PointerEvent) => {
      if (!caixa.current?.contains(e.target as Node)) setGaveta(false);
    };
    const escapou = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setGaveta(false);
    };
    document.addEventListener('pointerdown', foraDaqui);
    document.addEventListener('keydown', escapou);
    return () => {
      document.removeEventListener('pointerdown', foraDaqui);
      document.removeEventListener('keydown', escapou);
    };
  }, [gaveta]);

  /*
   * ── AS DUAS TECLAS QUE ABREM A BUSCA ──────────────────────────────────────
   *
   * `/` é a tecla que esta classe de produto ensinou a toda a gente, e **só
   * vale fora de um campo**: quem está a escrever uma anotação com uma barra no
   * meio da frase não quer abrir coisa nenhuma. `Ctrl`/`⌘`+`K` vale sempre,
   * porque ninguém a escreve por acaso.
   *
   * Sem área, não há escuta: a tecla não pode abrir uma porta que a tela não
   * desenha.
   */
  useEffect(() => {
    if (!area) return;
    const ouvir = (e: KeyboardEvent) => {
      const alvo = e.target;
      const escrevendo =
        alvo instanceof HTMLElement &&
        (alvo.tagName === 'INPUT' || alvo.tagName === 'TEXTAREA' || alvo.isContentEditable);
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setBusca(true);
      } else if (e.key === '/' && !escrevendo && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setBusca(true);
      }
    };
    document.addEventListener('keydown', ouvir);
    return () => document.removeEventListener('keydown', ouvir);
  }, [area]);

  /*
   * Os links do dono. Quem os passa é a vitrine; nas outras telas o cabeçalho
   * busca-os por si — ver `links-do-topo.ts` para o porquê de não ser a prop.
   */
  /*
   * Os links do dono E o interruptor do tutor, numa chamada e num pedido.
   *
   * Quem os passa é a vitrine (já os tem na resposta dela); nas outras dez rotas
   * o cabeçalho pergunta por si — ver `links-do-topo.ts` para o porquê de não ser
   * a prop, e para o porquê de ser UM hook e não dois.
   *
   * A ausência do interruptor lê-se como DESLIGADO: a porta do tutor é paga, e
   * uma porta que a tela desenha e o servidor recusa com 404 é pior do que porta
   * nenhuma.
   */
  const doTopo = useDoTopo(area, {
    ...(menu.length > 0 ? { menu } : {}),
    ...(typeof assistente === 'boolean' ? { assistente } : {}),
  });
  const itensDoMenu = doTopo.menu;
  const temAssistente = doTopo.assistente;

  /** Os links do dono, e as ações da conta — a MESMA lista nas duas larguras. */
  const linksDoDono = itensDoMenu.map((m) => ({
    chave: `${m.rotulo}-${m.url}`,
    rotulo: m.rotulo,
    url: m.url,
    externo: !m.url.startsWith('/'),
  }));

  return (
    <header
      /*
       * A porta da região `tema` — a única que a vitrine não tinha.
       *
       * `heroi`, `saudacao`, `prateleira` e `capa` já eram marcadas; `tema` não,
       * e o efeito era que a ficha de cores, cantos e marca d'água **não tinha
       * como ser aberta**: ela é o ramo final de `FichaDaRegiao`, alcançado só
       * por uma região que nenhum elemento declarava. O cabeçalho é onde a
       * pergunta "de quem é esta página?" se faz, e é onde o logo mora.
       *
       * Fora do Estúdio isto é HTML morto: nenhum byte de JavaScript, nenhum
       * estilo. Ver `lib/modo-estudio.ts`.
       */
      data-vst-regiao="tema"
      data-vst-rotulo="tema e marca"
      data-vst-cabecalho={cab.modelo}
      className={`${cab.fixo ? 'sticky top-0' : 'relative'} z-10 flex items-center gap-2 px-4 py-3 backdrop-blur sm:gap-3 sm:px-6 ${centrado ? 'flex-wrap justify-center' : ''}`}
      style={{ background: 'color-mix(in srgb, var(--fundo) 85%, transparent)' }}
    >
      {voltar && (
        /* O `←` também é um controle, e media 12×20. C2 não abre exceção para
           quem é pequeno por hábito tipográfico: `-ml-2` devolve o recuo óptico
           que o alvo de 44px acabaria criando na margem da página. */
        <Link
          to={voltar}
          aria-label={t('nav.voltar')}
          className={`${ALVO} -ml-2`}
          style={{ color: 'var(--texto-suave)' }}
        >
          <span aria-hidden>←</span>
        </Link>
      )}
      {logoDesenhavel ? (
        <img
          {...atributosDoLogo(logoDesenhavel, alturaDaMarca)}
          /* O `alt` é o NOME da área, e não «logo»: para quem usa leitor de tela
             e para quando a imagem não chega, o cabeçalho volta a ser texto —
             que é a mesma coisa que o ramo de baixo desenha. */
          alt={titulo}
          /* Altura fixa e largura livre: um logo é horizontal, e travar a largura
             o esmagaria conforme a proporção do arquivo que o cliente subiu. O
             `w-auto` é o que deixa o par `width`/`height` servir só de proporção
             (ver `atributosDoLogo`), reservando a caixa antes do byte chegar. */
          className={`w-auto shrink-0 object-contain ${centrado ? 'mx-auto basis-full max-w-[60%] object-center' : 'max-w-[40%] object-left'}`}
          style={{ height: alturaDaMarca }}
        />
      ) : (
        /*
         * `max-w-[45%]` é o que faz o `truncate` funcionar.
         *
         * Ele já era `shrink-0 truncate`, e as duas classes se anulam: um item
         * que não encolhe nunca fica menor que o texto, então `text-overflow`
         * não tem o que cortar. Numa trilha chamada "Trilha Vibe Stack N8N" o
         * nome empurrava sozinho a barra inteira para além de 390px — foi essa
         * linha, com o alvo de 44px das ações, que produziu os 497px medidos.
         * Com o teto, o nome cede antes de a página ceder.
         */
        <span
          className={`shrink-0 truncate font-medium ${centrado ? 'basis-full w-full text-center' : 'max-w-[45%]'}`}
          style={{ fontSize: Math.round(alturaDaMarca * 0.7) }}
        >
          {titulo}
        </span>
      )}

      {/* As portas, coladas ao nome — é aqui que a página começa a subir. O
          `flex-1` saiu do título e veio para cá: o nome ocupa o que precisa, e é
          este bloco que absorve a sobra. */}
      <div className={`min-w-0 ${centrado ? 'flex justify-center' : 'flex-1'}`}>{portas}</div>

      {/* A pastilha do encontro, **antes** da busca — o «lado esquerdo de
          procurar» da ordem do dono. Ela está fora do rolador das portas de
          propósito: ali, a 390 px, ficaria depois da borda direita. Ver o
          cabeçalho de `PastilhaDoEncontro`. */}
      {encontro}

      {/*
       * ── A BUSCA: um CAMPO no desktop, uma lente no telemóvel ──────────────
       *
       * Não é um ícone de lupa solto. Uma lupa sozinha é um palpite; uma caixa
       * com a palavra dentro diz «escreva aqui e alguma coisa acontece» antes
       * de qualquer clique — e a tecla impressa à direita ensina o atalho sem
       * um passeio guiado.
       *
       * Abaixo de 768 px a caixa vira alvo de 44×44 com a lente: ali a largura
       * é disputada por quatro controlos e uma marca (ver as duas barras, mais
       * abaixo), e uma caixa de 180 px empurraria a linha para fora da janela —
       * que é o defeito de 497 px que este cabeçalho já mediu uma vez.
       */}
      {area && (
        <>
          <button
            type="button"
            data-vst-abrir-busca=""
            onClick={() => setBusca(true)}
            /* «uma aula» era meia verdade desde 24/09: o painel acha pelo nome
               E pelo assunto, e quem procura «como usar o node loop» não está a
               procurar uma aula, está a procurar onde aquilo é falado. */
            aria-label={t('busca.procurarNasAulas')}
            /* `min-w-[44px]` junto com a altura, como o resto da barra: sem ele
               a lente do telemóvel media **32×44** — alta o bastante e estreita
               demais, que é metade do defeito que C2 existe para não repetir. */
            /*
             * `vst-porta-da-busca` é uma classe de FOLHA e não um `style`, por
             * uma razão só: a moldura existe acima de 768 px e não existe
             * abaixo, e uma propriedade em linha não sabe de largura. Acima
             * dela a coisa é lida como **campo** — é a moldura que explica a
             * tecla impressa ao lado; abaixo, a mesma moldura disputaria a
             * barra com quatro controlos e uma marca, e o que resta é a lente.
             */
            className="vst-porta-da-busca inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-2 rounded-[var(--raio)] px-2 text-sm md:min-w-[200px] md:justify-start md:px-3"
            style={{ color: 'var(--texto-suave)' }}
          >
            <svg
              aria-hidden
              viewBox="0 0 16 16"
              width={16}
              height={16}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              className="shrink-0"
            >
              <circle cx="7" cy="7" r="4.6" />
              <path d="M10.4 10.4 14 14" />
            </svg>
            <span className="hidden md:inline">{t('nav.procurar')}</span>
            <span
              aria-hidden
              className="ml-auto hidden rounded px-1.5 text-[11px] leading-[18px] md:inline"
              style={{
                border: '1px solid color-mix(in srgb, var(--texto-suave) 30%, transparent)',
              }}
            >
              /
            </span>
          </button>
          {busca && (
            /* `fallback={null}`: entre o clique e o pedaço há um quadro, e um
               «Carregando…» a cobrir a página inteira nesse quadro pisca mais
               do que informa. */
            <Suspense fallback={null}>
              <PainelDeBusca areaSlug={area} aoFechar={() => setBusca(false)} />
            </Suspense>
          )}
          {/*
           * ── A PORTA DO TUTOR, FLUTUANTE ────────────────────────────────
           *
           * Ela é `position: fixed`, então desenha-se no canto da JANELA e não
           * aqui dentro. Mora neste ficheiro pela razão que pôs a busca aqui: o
           * cabeçalho é a única mobília que TODA tela da área tem, e é o único
           * sítio onde uma porta nova nasce em onze rotas em vez de numa.
           *
           * Ela decide sozinha que não existe em `/aula/:id` — ali já há o trilho
           * e a alça — e sem `assistente` não desenha nada.
           */}
          <PortaFlutuante areaSlug={area} assistente={temAssistente} />
        </>
      )}

      {/*
       * ## Por que existem duas barras, e não uma que se adapta
       *
       * Porque em 390px de largura não cabem seis links do dono, `Áreas`,
       * `Conta` e `Sair` com 44px de alvo cada — e a rodada anterior provou isso
       * com número: dar o alvo mínimo aos quatro controles do canto levou a
       * vitrine a 406px de conteúdo e a trilha a 497px, numa janela de 390. A
       * saída **não** é devolver o alvo de 20px (seria trocar C2 por C1); é
       * parar de exigir que quatro rótulos e uma marca dividam uma linha que não
       * tem largura para eles.
       *
       * Acima de 640px tudo continua exatamente como estava: os rótulos à vista,
       * um alvo de 44px cada. Abaixo, os mesmos itens — **e os links do dono,
       * que antes simplesmente sumiam no celular** — moram numa gaveta atrás de
       * um botão de 44×44. Nada perde o nome; o que muda é quantos nomes a
       * barra mostra ao mesmo tempo.
       */}
      <nav
        className={`${semBarra ? 'hidden' : 'hidden items-center gap-1 sm:flex'} ${centrado ? 'basis-full justify-center' : ''}`}
        aria-label={t('nav.contaELinks')}
      >
        {linksDoDono.map((m) => (
          <a
            key={m.chave}
            href={m.url}
            /* Link externo abre em aba nova: tirar o aluno da aula é o único
               jeito de esta escolha errar de verdade. Caminho interno navega
               na mesma aba, que é o que se espera de `/suporte`. */
            {...(m.externo ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
            className={ALVO}
            style={{ color: 'var(--texto-suave)' }}
          >
            {m.rotulo}
          </a>
        ))}
        {acoes && (
          <>
            <Link to="/" className={ALVO} style={{ color: 'var(--texto-suave)' }}>
              {t('nav.areas')}
            </Link>
            {/* A porta do perfil. Sem ela, `PATCH /aluno/api/eu` — que troca nome e
                senha — continuava sem chamador nenhum, e quem definiu uma senha
                fraca no primeiro acesso não tinha como trocá-la. */}
            <Link to="/perfil" className={ALVO} style={{ color: 'var(--texto-suave)' }}>
              {t('nav.conta')}
            </Link>
            {/* Apaga-se sozinho onde a área é monolingue — que são todas até o
                tutor habilitar a segunda língua. Ver `EscolhaDoIdioma`. */}
            {<EscolhaDoIdioma className={ALVO} />}
            <button
              type="button"
              onClick={() => void sair()}
              className={ALVO}
              style={{ color: 'var(--texto-suave)' }}
            >
              {t('nav.sair')}
            </button>
          </>
        )}
      </nav>

      {(acoes || linksDoDono.length > 0) && (
        <div
          className={`relative shrink-0 ${semBarra ? '' : 'sm:hidden'} ${centrado ? 'absolute right-4 top-3 sm:right-6' : ''}`}
          ref={caixa}
        >
          <button
            type="button"
            onClick={() => setGaveta((v) => !v)}
            aria-expanded={gaveta}
            aria-controls={gavetaId}
            aria-label={t('nav.menu')}
            className={ALVO}
            style={{ color: 'var(--texto-suave)' }}
          >
            <span aria-hidden className="text-lg leading-none">
              ☰
            </span>
          </button>

          {gaveta && (
            <div
              id={gavetaId}
              className="absolute right-0 top-full z-20 mt-1 flex w-56 flex-col gap-0.5 p-1"
              style={{
                background: 'var(--fundo)',
                border: '1px solid color-mix(in srgb, var(--texto-suave) 30%, transparent)',
                borderRadius: 'var(--raio)',
                boxShadow: '0 12px 32px rgba(0,0,0,.35)',
              }}
            >
              {linksDoDono.map((m) => (
                <a
                  key={m.chave}
                  href={m.url}
                  {...(m.externo ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
                  onClick={() => setGaveta(false)}
                  className={LINHA_DA_GAVETA}
                  style={{ color: 'var(--texto-suave)' }}
                >
                  {m.rotulo}
                </a>
              ))}
              {acoes && (
                <>
                  <Link
                    to="/"
                    onClick={() => setGaveta(false)}
                    className={LINHA_DA_GAVETA}
                    style={{ color: 'var(--texto-suave)' }}
                  >
                    {t('nav.areas')}
                  </Link>
                  <Link
                    to="/perfil"
                    onClick={() => setGaveta(false)}
                    className={LINHA_DA_GAVETA}
                    style={{ color: 'var(--texto-suave)' }}
                  >
                    {t('nav.conta')}
                  </Link>
                  <EscolhaDoIdioma
                    aoEscolher={() => setGaveta(false)}
                    className={LINHA_DA_GAVETA}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setGaveta(false);
                      void sair();
                    }}
                    className={LINHA_DA_GAVETA}
                    style={{ color: 'var(--texto-suave)' }}
                  >
                    {t('nav.sair')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </header>
  );
}
